import {action, computed, observable, override, autorun, makeObservable, runInAction} from 'mobx';

import {BaseViewModel} from "../../../../infrastructure/models/base-view-model";
import {APP, GAME_STATUSES, GAME_TIME_AND_STATUS_DISPLAY_TYPE, LINEUPS_STATUS, SPORT_TYPES, TENNIS_SCOREBOARD} from "../../../app/consts";
import {AppTerms} from "../../../app/app-terms";
import {GameCardCompetitorViewModel} from "./game-card.competitor.view-model";
import {PartialGameDomainModel} from "../../../../domain/partial-game/models/partial-game-domain-model";
import {GameCenterLinkModel} from "../../../../containers/game-center-container/models/game-center.link-model";
import {ODDS_TYPES} from "../../odds/components/odds-consts";
import {OddsViewModel} from "../../../../domain/common/odds/models/odds.view-model";
import {BaseGameTemplateAnimationSetModel} from "../../../../infrastructure/models/animation/game/templates/base-game-template-animation-set-model";
import {GameCardAnimationService} from "../animations/game-card-animation-service";
import {AnimationService} from "../../../../infrastructure/models/animation/animation-service";
import {GameCenterHashParamsModel} from "../../../../containers/game-center-container/models/game-center.hash-params-model";
import {AnimationDomainModel} from "../../../../domain/common/animation/animation.domain-model";
import {GameCardFutureStreamingAssetsRequestParams} from "../assets/game-card-future-streaming-assets.request-params";
import {FieldAssetsRequestParams} from "../../../icons/field";
import {GameCardLiveStreamingAssetsRequestParams} from "../assets/game-card-live-streaming-assets.request-params";
import {GameCardIconModel} from "./game-card.icon-model";
import {GAME_CARD} from "../game-card.consts";
import {OddsPreviewViewModel} from "../../../../domain/common/odds-preview/models/odds-preview.view-model";
import {OpportunityViewModel} from "../../../../domain/bets-opportunities/models/opportunities/opportunity.view-model";
import {GameCardOpportunityViewModel} from "./game-card.opportunity.view-model";
import {useThemeColor} from "../../../../infrastructure/hooks/useThemeColor";
import {COLORS} from "../../../app/consts/colors";
import {GameCardScoreboardViewModel} from "./game-card.scoreboard.view-model";
import {StageDomainModel} from "../../../../domain/full-game/models/stage/stage-domain-model";

export class GameCardViewModel extends BaseViewModel {
  domainModel: PartialGameDomainModel;
  terms: AppTerms;
  isSmallView: Boolean;
  date: String;
  time: String;
  @observable oddsType: ODDS_TYPES;
  @observable animation: BaseGameTemplateAnimationSetModel;
  animationService: GameCardAnimationService;
  @observable isLastVisitedGame: Boolean;
  roundName: String;
  stageName: String;
  groupName: String;

  constructor(partialGameDomainModel: PartialGameDomainModel, terms: AppTerms, isSmallView: Boolean, date, time, oddsType: ODDS_TYPES, animationService: AnimationService, isLastVisitedGame: Boolean = false) {
    super(partialGameDomainModel);

    this.terms = terms;
    this.isSmallView = isSmallView;
    this.date = date;
    this.time = time;
    this.oddsType = oddsType;
    this.animationService = animationService || new GameCardAnimationService();
    this.isLastVisitedGame = isLastVisitedGame;
    this.roundName = partialGameDomainModel?.roundName;
    this.stageName = partialGameDomainModel?.stageName;
    this.groupName = partialGameDomainModel?.groupName;
    this.hideRedCard = false;

    makeObservable(this);
  }

  get startTime(): Date {
    return this.domainModel.startTime;
  }

  get matchupId(): Boolean {
    return this.domainModel.matchupId;
  }

  get isFollow(): Boolean {
    return this.domainModel.isFollow;
  }

  @computed get lineupsTooltipTitle() {
    return this.domainModel.hasLineups ? this.terms.SEE_CONFIRMED_LINEUPS : this.terms.SEE_INJURIES_AND_SUSPENSIONS;
  }

  @computed get lineupsStatus() {
    return this.domainModel.lineupsStatus;
  }

  @computed get lineupsStatusText() {
    return this.domainModel.lineupsStatusText;
  }

  @computed get gameLinkModel() {
    const hashParams: GameCenterHashParamsModel = new GameCenterHashParamsModel({id: this.id});
    const linkModel: GameCenterLinkModel = new GameCenterLinkModel(this.matchupId, this.domainModel.competition.sport.nameForURL, this.domainModel.competition.nameForURL, this.nameForUrl, hashParams, this.isFollow);

    return linkModel;
  }

  get sportId() {
    return this.domainModel.sport.id;
  }

  @computed get shouldDisplayTime() {
    const shouldDisplayTimeByStatusGroup = {
      [GAME_STATUSES.anticipated]: this.domainModel.startTime,
      [GAME_STATUSES.started]: !this.hasScore,
      [GAME_STATUSES.ended]: !this.hasScore,
      [GAME_STATUSES.scheduled]: true,
    };

    return shouldDisplayTimeByStatusGroup[this.domainModel.statusGroup];
  }

  @computed get scoreText() {
    return `${this.homeCompetitor.score} - ${this.awayCompetitor.score}`;
  }

  @computed get hasScore() {
    return this.domainModel.homeCompetitor.hasScore;
  }

  @computed get competitorInPossessionIndex() {
    let competitorInPossessionIndex = -1;
    if (this.homeCompetitor.inPossession) {
      competitorInPossessionIndex = 0;
    }
    else if (this.awayCompetitor.inPossession) {
      competitorInPossessionIndex = 1;
    }

    return competitorInPossessionIndex;
  }

  @computed get statusGroup() {
    return this.domainModel.statusGroup;
  }

  @computed get iconTvActive() {
    return this.domainModel.hasTVNetworks && this.isLive;
  }

  @computed get iconTvVisible() {
    return this.domainModel.hasTVNetworks;
  }

  @computed get iconLiveStreamingVisible() {
    return this.domainModel.isLiveStreaming;
  }

  @computed get iconInjuriesAndSuspensionsVisible() {
    return this.domainModel.statusGroup === GAME_STATUSES.scheduled && ((this.sportId === SPORT_TYPES.football && this.lineupsStatus === LINEUPS_STATUS.probableLineups) || this.domainModel.hasMissingPlayers);
  }

  @computed get shouldRenderIconField() {
    const hasLineups: Boolean = this.sportId === SPORT_TYPES.football && this.lineupsStatus === LINEUPS_STATUS.confirmedLineups && this.domainModel.statusGroup === GAME_STATUSES.scheduled;
    // const hasBoxScores: Boolean = this.isAmericanSport && this.domainModel.statusGroup === GAME_STATUSES.started && this.domainModel.hasLineups;

    let shouldRenderIconField: Boolean = hasLineups; // || hasBoxScores;

    return shouldRenderIconField
  }

  @computed get shouldRenderProbableIconField() {
    const shouldRenderProbableIconField: Boolean = this.sportId === SPORT_TYPES.football && this.lineupsStatus === LINEUPS_STATUS.probableLineups;

    return shouldRenderProbableIconField;
  }

  get description() {
    return this.domainModel.winDescription;
  }

  @computed get aggregateScore() {
    return this.domainModel.aggregateScore;
  }

  get aggregateText() {
    return this.isSmallView ? this.domainModel.shortAggregateText : this.domainModel.aggregateText;
  }

  @computed get isLive() {
    return this.domainModel.isLive;
  }

  @computed get isEnded() {
    return this.domainModel.isEnded;
  }

  @computed get isScheduled() {
    return this.domainModel.isScheduled;
  }

  @computed get awayCompetitor() {
    return new GameCardCompetitorViewModel(this.domainModel.awayCompetitor, this.terms, this.isSmallView)
  }

  @computed get homeCompetitor() {
    return new GameCardCompetitorViewModel(this.domainModel.homeCompetitor, this.terms, this.isSmallView)
  }

  @computed get shouldRenderAggregateStarOnText() {
    return !this.awayCompetitor.isQualified && !this.homeCompetitor.isQualified;
  }

  get competitionId() {
    return this.domainModel.competition.id;
  }

  @computed get gameTimeAndStatusDisplayType() {
    return this.domainModel.gameTimeAndStatusDisplayType;
  }

  @computed get status() {
    const animationStatus = this.animation?.animationText;
    const status = this.gameTimeAndStatusDisplayType === GAME_TIME_AND_STATUS_DISPLAY_TYPE.statusText ? this.gameTimeAndStatusText : this.gameTimeAndShortStatusText?.replace?.('\'', '');

    return animationStatus || status;
  }

  @computed get shortStatus() {
    const animationStatus = this.animation?.animationText;
    const shortStatusText = this.gameTimeAndShortStatusText?.replace?.('\'', '');

    return animationStatus || shortStatusText;
  }

  get id(): Number {
    return this.domainModel.id;
  }

  get nameForUrl(): String {
    return this.domainModel.nameForUrl;
  }

  @computed get iconLineupActive(): String {
    return this.domainModel.hasLineups;
  }

  @computed get gameTimeAndStatusText(): String {
    return this.domainModel.gameTimeAndStatusText;
  }

  @computed get gameTimeAndShortStatusText(): String {
    return this.domainModel.gameTimeAndShortStatusText;
  }

  @computed get statusText(): String {
    return this.domainModel.statusText;
  }

  @computed get justEnded(): Boolean {
    return this.domainModel.justEnded;
  }

  get round(): String {
    return this.domainModel.roundNum;
  }

  @computed get isHomeCompetitorPlayerNameText(): String {
    return this.animation?.isHomeCompetitor && this.animation.playerName;
  }

  @computed get isAwayCompetitorPlayerNameText(): String {
    return this.animation?.isAwayCompetitor && this.animation.playerName;
  }

  @computed get homeCompetitorPlayerNameAnimationClassName(): String {
    return this.animation?.homeCompetitorPlayerName?.currentAnimationClassName;
  }

  @computed get awayCompetitorPlayerNameAnimationClassName(): String {
    return this.animation?.awayCompetitorPlayerName?.currentAnimationClassName;
  }

  @computed get homeCompetitorScoreAnimationClassName(): String {
    return this.animation?.homeCompetitorScore?.currentAnimationClassName;
  }

  @computed get awayCompetitorScoreAnimationClassName(): String {
    return this.animation?.awayCompetitorScore?.currentAnimationClassName;
  }

  @computed get isCompetitorScoreDelay() {
    return this.animation?.homeCompetitorScore?.delayMS || this.animation?.awayCompetitorScore?.delayMS;
  }

  @computed get shouldDisplayTimeDelay() {
    return this.animation ? (this.shouldDisplayTime ? 0 : 4500) : 0;
  }

  @computed get gameTimeAnimationClassName() {
    return this.animation?.gameTime?.currentAnimationClassName
  }

  @computed get homeCompetitorNameAnimationClassName(): String {
    return this.animation?.homeCompetitorName?.currentAnimationClassName;
  }

  @computed get awayCompetitorNameAnimationClassName(): String {
    return this.animation?.awayCompetitorName?.currentAnimationClassName;
  }

  @computed get homeCompetitorLogoAnimationClassName(): String {
    return this.animation?.homeCompetitorLogo?.currentAnimationClassName;
  }

  @computed get awayCompetitorLogoAnimationClassName(): String {
    return this.animation?.awayCompetitorLogo?.currentAnimationClassName;
  }

  @computed get homeCompetitorEventLogoAnimationClassName(): String {
    return this.animation?.homeCompetitorEventLogo?.currentAnimationClassName;
  }

  @computed get awayCompetitorEventLogoAnimationClassName(): String {
    return this.animation?.awayCompetitorEventLogo?.currentAnimationClassName;
  }

  @computed get homeCompetitorEventCardLogoAnimationClassName(): String {
    return this.animation?.homeCompetitorEventCardLogo?.currentAnimationClassName;
  }

  @computed get awayCompetitorEventCardLogoAnimationClassName(): String {
    return this.animation?.awayCompetitorEventCardLogo?.currentAnimationClassName;
  }

  @computed get statusEventLogoAnimationClassName(): String {
    return this.animation?.statusEventLogo?.currentAnimationClassName;
  }

  @computed get statusAndTimeText(): String {
    return this.animation ? (this.animation.statusText.currentAnimationClassName ? this.status : this.time) : this.status || this.time;
  }

  @computed get shortStatusAndTimeText(): String {
    return this.animation ? (this.animation.statusText.currentAnimationClassName ? this.shortStatus : this.time) : this.shortStatus || this.time;
  }

  @computed get odds(): OddsViewModel {
    if (!this.domainModel.odds) {
      return null;
    }

    const odds: OddsViewModel = new OddsViewModel(this.domainModel.odds, this.oddsType);

    return odds;
  }

  @computed get oddsPreview(): OddsPreviewViewModel {
    if (!this.domainModel.oddsPreview) {
      return null;
    }

    const oddsPreview: OddsPreviewViewModel = new OddsPreviewViewModel(this.domainModel.oddsPreview);

    return oddsPreview;
  }

  @computed get hasOdds(): Boolean {
    return !!this.odds || !!this.oddsPreview;
  }

  @computed get hasCurrentValidRates(): Boolean {
    let hasValidRates: Boolean = this.odds?.isValidCurrentRateOptions || this.oddsPreview?.cellsRates?.length > 0

    return hasValidRates;
  }

  @action createAnimation(animation: AnimationDomainModel) {
    if (!!this.animation && this.animation?.uniqueId === this.domainModel?.animation?.uniqueId) {
      return;
    }

    this.animation?.stop();

    runInAction(() => {
      this.animation = this.animationService.start(this.domainModel.animation || animation);
    })

    autorun(() => {
      if (this.animation?.isEnded) {

        runInAction(() => {
          this.animation = null;
        })
        this.domainModel.clearAnimation();
      }
    })
  }

  @override update(model: PartialGameDomainModel) {
    this.domainModel = model;
  }

  get confirmedLineupsIconModel(): GameCardIconModel {
    return new GameCardIconModel(new FieldAssetsRequestParams(GAME_CARD.ICON_SIZE.width, GAME_CARD.ICON_SIZE.height, useThemeColor(COLORS.accentPrimary), this.domainModel.competition.sport.nameForURL), this.isAmericanSport ? this.terms.WEB_TOOLTIP_SEE_LIVE_BOXSCORE : this.terms.SEE_CONFIRMED_LINEUPS)
  }

  // get notConfirmedLineupsIconModel(): GameCardIconModel {
  //   return new GameCardIconModel(new FieldAssetsRequestParams(GAME_CARD.ICON_SIZE.width, GAME_CARD.ICON_SIZE.height, useThemeColor(COLORS.textSecondary), this.domainModel.competition.sport.nameForURL), this.terms.SEE_PROBABLE_LINEUPS);
  // }

  @computed get lineupsIconModel(): GameCardIconModel {
    let icon: GameCardIconModel = null;

    if(this.shouldRenderIconField) {
      icon = this.confirmedLineupsIconModel;
    }
    // else if (this.shouldRenderProbableIconField){
    //   icon = this.notConfirmedLineupsIconModel;
    // }

    return icon;
  }

  @computed get hasLiveStreaming(): Boolean {
    return (!this.isSmallView && this.domainModel.hasLiveStreaming) || (this.isSmallView && this.domainModel.hasLiveStreaming && this.domainModel.statusGroup === GAME_STATUSES.started);
  }

  @computed get isFutureStreaming(): Boolean {
    return this.domainModel.hasLiveStreaming && this.domainModel.statusGroup === GAME_STATUSES.scheduled;
  }

  @computed get isLiveStreaming(): Boolean {
    return this.domainModel.hasLiveStreaming && this.domainModel.statusGroup === GAME_STATUSES.started;
  }

  get liveStreamingIconModel(): GameCardIconModel {
    return new GameCardIconModel(new GameCardLiveStreamingAssetsRequestParams(GAME_CARD.ICON_SIZE.width, GAME_CARD.ICON_SIZE.height, false), this.terms.WATCH_LIVE_LANDING_PAGE_GAME);
  }

  get futureStreamingIconModel(): GameCardIconModel {
    return new GameCardIconModel(new GameCardFutureStreamingAssetsRequestParams(GAME_CARD.ICON_SIZE.width, GAME_CARD.ICON_SIZE.height, false), this.terms.WATCH_LIVE_AVAILABLE);
  }

  @computed get streamingIconModel(): GameCardIconModel {
    const icon = this.isFutureStreaming ? this.futureStreamingIconModel : this.liveStreamingIconModel;
    return icon;
  }

  get isCompetitorBadgeVisible(): Boolean {
    return true
  }

  get shouldRenderBadge(): Boolean {
    const validStatus: Array<Number> = [GAME_TIME_AND_STATUS_DISPLAY_TYPE.gameTime, GAME_TIME_AND_STATUS_DISPLAY_TYPE.gameTimeAndStatusText];
    const shouldRenderBadge: Boolean = !this.animation && validStatus.includes(this.gameTimeAndStatusDisplayType);

    return shouldRenderBadge
  }

  get shouldRenderQualifiedStar(): Boolean {
    return true;
  }

  get hasRedCards(): Boolean {
    return this.homeCompetitor.hasRedCards || this.awayCompetitor.hasRedCards || this.animation?.eventLogo;
  }

  @computed get isAmericanSport(): Boolean {
    const americanSportIds: Array<Number> = [SPORT_TYPES.basketball, SPORT_TYPES.hockey, SPORT_TYPES.americanFootball, SPORT_TYPES.baseball];
    const isAmericanSport: Boolean = americanSportIds.includes(this.sportId);

    return isAmericanSport;
  }

  @computed get isAnticipatedGame(): Boolean {
    return this.statusGroup === GAME_STATUSES.anticipated
  }

  @computed get bettingOpportunity(): OpportunityViewModel {
    return this.domainModel.bettingOpportunity ? new GameCardOpportunityViewModel(this.domainModel.bettingOpportunity) : null
  }


  @computed get presentationType(): APP.ANALYTICS.PRESENTATION_TYPE {
    return this.bettingOpportunity ? APP.ANALYTICS.PRESENTATION_TYPE.textualOdds : APP.ANALYTICS.PRESENTATION_TYPE.odds;
  }

  get metaDataStartTime(): String {
    return this.domainModel.apiModel.startTime;
  }

  get metaDataMatchName(): String {
    return `${this.homeCompetitor.name} VS ${this.awayCompetitor.name}`;
  }

  get metaDataSportName(): String {
    return this.domainModel.sport.name;
  }

  get metaDataScopeName(): String {
    return "https://schema.org/SportsEvent";
  }

  get metaDataLocationName(): String {
    const location = (this.domainModel?.venue?.name || this.domainModel.competition?.countryName);

    return location;
  }

    get metaDataScopePlace(): String {
    return "https://schema.org/Place";
  }

  get popularityRank(): Number {
    const popularityRank: Number = this.homeCompetitor.domainModel.popularityRank + this.awayCompetitor.domainModel.popularityRank;

    return popularityRank;
  }

  get isCancelledOrPostponed(): Boolean {
    return this.domainModel.isEnded && !this.domainModel.hasScore;
  }

  get tvNetworkName(): Boolean {
    return this.domainModel.tvNetworkName;
  }

  @computed get showTennisScoreboard(): Boolean {
    return this.statusGroup === GAME_STATUSES.started && this.sportId === SPORT_TYPES.tennis && this.domainModel.stages?.length > 0;
  }

  @computed get scoreboard(): Array<GameCardScoreboardViewModel> {
    const validScoreboardStages = this.domainModel.stages?.filter(stage=> stage.id !== TENNIS_SCOREBOARD.CURRENT_GAME_SCORE_ID)?.sort((a, b) => (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0));
    const scoreboardStages = validScoreboardStages?.map(stage => new GameCardScoreboardViewModel(stage, this.competitorInPossessionIndex));

    return scoreboardStages;
  }

  @computed get currentGameScore(): String {
    let currentGameScoreString = null;
    const currentGameScore:StageDomainModel = this.domainModel.stages?.first(stage=> stage.id === TENNIS_SCOREBOARD.CURRENT_GAME_SCORE_ID);
    if(currentGameScore) {
      const homeScore = currentGameScore.homeCompetitorScore === TENNIS_SCOREBOARD.ADVANTAGE_SCORE.score ? TENNIS_SCOREBOARD.ADVANTAGE_SCORE.value : currentGameScore.homeCompetitorScore;
      const awayScore = currentGameScore.awayCompetitorScore === TENNIS_SCOREBOARD.ADVANTAGE_SCORE.score ? TENNIS_SCOREBOARD.ADVANTAGE_SCORE.value : currentGameScore.awayCompetitorScore;
      currentGameScoreString = `${homeScore} : ${awayScore}`;
    }

    return currentGameScoreString;

  }

  @computed get hasScoreboardFullView(): Boolean {
    return this.currentGameScore && this.scoreboard?.length > 0;
  }
}
